import firebase from './firebaseConfig';
import TrackFile from './models/TrackFile';

// In-flight uploads kept here so they stay visible — with progress — even
// when their project isn't the one currently open. A folder-drop import
// creates several projects at once and uploads to all of them in the
// background; this is what lets those tracks show the moment you navigate in.
//
// Shape: { [projectId]: { [trackId]: { track, progress } } }
const registry = {};
const listeners = new Set();
const emit = () => listeners.forEach((fn) => fn());

// Reported through setProgress in place of a percentage to signal a
// terminal state.
const UPLOAD_DONE = 200;
const UPLOAD_FAILED = 500;

// Progress events fire many times per second per file; coalesce them so a big
// import doesn't trigger a re-render storm. Structural changes emit at once.
let emitScheduled = false;
const emitThrottled = () => {
    if (emitScheduled) return;
    emitScheduled = true;
    setTimeout(() => { emitScheduled = false; emit(); }, 120);
};

export function subscribeUploads(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
}

export function getProjectUploads(projectId) {
    const entry = projectId ? registry[projectId] : null;
    const tracks = [];
    const progress = {};
    if (entry) {
        for (const id of Object.keys(entry)) {
            tracks.push(entry[id].track);
            progress[id] = entry[id].progress;
        }
    }
    return { tracks, progress };
}

// Backend caps a batch at 50; 25 keeps each signed-URL call quick to sign.
const PUT_BATCH_SIZE = 25;
// Simultaneous byte uploads to Storage. GCS scales fine — this cap just
// avoids opening dozens of sockets at once.
const PUT_CONCURRENCY = 6;

// A 503 from an overwhelmed Cloud Function surfaces to the SDK as
// 'internal'; these codes are all transient, so a jittered retry recovers
// instead of failing the file. Quota / permission errors are NOT retried.
const RETRYABLE_CODES = new Set(['internal', 'unavailable', 'deadline-exceeded']);

async function callWithRetry(name, data, attempts = 4) {
    const fn = firebase.functions().httpsCallable(name);
    for (let attempt = 0; ; attempt++) {
        try {
            return await fn(data);
        } catch (err) {
            const code = (err?.code || '').replace('functions/', '');
            if (attempt >= attempts - 1 || !RETRYABLE_CODES.has(code)) throw err;
            // Exponential backoff with jitter so retries don't re-sync into
            // a fresh burst against the function.
            const backoff = 500 * (3 ** attempt) * (0.5 + Math.random());
            await new Promise((r) => setTimeout(r, backoff));
        }
    }
}

// Runs `worker` over `items` with at most `limit` in flight at once.
async function runPool(items, limit, worker) {
    let cursor = 0;
    const runner = async () => {
        while (cursor < items.length) {
            await worker(items[cursor++]);
        }
    };
    await Promise.all(
        Array.from({ length: Math.min(limit, items.length) }, runner),
    );
}

const UPLOAD_ERROR_MESSAGES = {
    'resource-exhausted': 'Upload exceeds your storage plan limit.',
    'permission-denied': "You don't have edit access on this project.",
};

// Uploads any number of files into one project: batched Cloud Function
// calls (chunked + retried) and concurrency-limited byte uploads. Returns
// the optimistic Track objects so callers can render rows immediately.
export async function startBulkUpload(project, files) {
    if (!files?.length) return [];
    const trackFiles = files.map((file) => new TrackFile(file, project));

    // Resolve durations and register every track up front so the whole
    // import appears in the UI before any network work begins.
    const tracks = await Promise.all(trackFiles.map((tf) => tf.asTrack()));
    if (!registry[project.id]) registry[project.id] = {};
    trackFiles.forEach((tf, i) => {
        registry[project.id][tf.trackId] = { track: tracks[i], progress: 0 };
    });
    emit();

    const setProgress = (trackId, progress) => {
        const entry = registry[project.id];
        if (!entry || !entry[trackId]) return;
        if (progress === UPLOAD_DONE || progress === UPLOAD_FAILED) {
            delete entry[trackId];
            if (!Object.keys(entry).length) delete registry[project.id];
            emit();
        } else {
            entry[trackId].progress = progress;
            emitThrottled();
        }
    };

    // Upload in the background — the caller (and its project) must render
    // immediately off the optimistic tracks; progress flows via the registry.
    (async () => {
        const ctx = { failed: [], errorCode: null };
        for (let i = 0; i < trackFiles.length; i += PUT_BATCH_SIZE) {
            await uploadChunk(
                project, trackFiles.slice(i, i + PUT_BATCH_SIZE), setProgress, ctx,
            );
        }
        if (ctx.failed.length) {
            const specific = ctx.errorCode && UPLOAD_ERROR_MESSAGES[ctx.errorCode];
            alert(specific || (ctx.failed.length === 1
                ? `"${ctx.failed[0]}" failed to upload. Please try again.`
                : `${ctx.failed.length} of ${trackFiles.length} files failed to upload. Please try again.`));
        }
    })().catch((err) => console.error('Bulk upload error:', err));

    return tracks;
}

function fail(ctx, setProgress, trackFile, err) {
    const code = (err?.code || '').replace('functions/', '');
    if (code && !ctx.errorCode) ctx.errorCode = code;
    ctx.failed.push(trackFile.name);
    setProgress(trackFile.trackId, UPLOAD_FAILED);
}

async function uploadChunk(project, chunk, setProgress, ctx) {
    const artistId = project.artist.id;
    const projectId = project.id;

    // 1. One signed-URL call for the whole chunk.
    let putByTrack;
    try {
        const res = await callWithRetry('getGranteePutAudioURLs', {
            artistId, projectId, items: chunk.map((tf) => tf.putItem()),
        });
        putByTrack = new Map((res.data?.results || []).map((r) => [r.trackId, r]));
    } catch (err) {
        console.error('Batch signed-URL request failed:', err);
        chunk.forEach((tf) => fail(ctx, setProgress, tf, err));
        return;
    }

    // 2. Upload bytes — concurrency-limited. Bytes go straight to Storage,
    //    which scales independently of the Cloud Functions.
    const uploaded = [];
    await runPool(chunk, PUT_CONCURRENCY, async (tf) => {
        try {
            const put = putByTrack.get(tf.trackId);
            if (!put) throw new Error('No signed URL returned for track.');
            setProgress(tf.trackId, 0);
            await tf.putBytes(put, (pct) => setProgress(tf.trackId, pct));
            uploaded.push(tf);
        } catch (err) {
            console.error('Track byte upload failed:', tf.name, err);
            fail(ctx, setProgress, tf, err);
        }
    });
    if (!uploaded.length) return;

    // 3. One metadata call for everything that uploaded.
    let metaByTrack;
    try {
        const items = await Promise.all(uploaded.map((tf) => tf.metadataItem()));
        const res = await callWithRetry('getTrackMetadataBatch', { artistId, projectId, items });
        if (!res.data?.res) throw new Error('getTrackMetadataBatch failed.');
        metaByTrack = new Map((res.data.results || []).map((r) => [r.trackId, r.data]));
    } catch (err) {
        console.error('Batch metadata request failed:', err);
        uploaded.forEach((tf) => fail(ctx, setProgress, tf, err));
        return;
    }

    // 4. Write each track entry to RTDB.
    await Promise.all(uploaded.map(async (tf) => {
        try {
            const metadata = metaByTrack.get(tf.trackId);
            if (!metadata) throw new Error('No metadata returned for track.');
            metadata.revisions[tf.revId].metadata.uploaded = true;
            await firebase.database().ref('artists').child(artistId)
                .child('projects').child(projectId)
                .child('tracks').child(tf.trackId).set(metadata);
            setProgress(tf.trackId, UPLOAD_DONE);
        } catch (err) {
            console.error('Track RTDB write failed:', tf.name, err);
            fail(ctx, setProgress, tf, err);
        }
    }));
}
